import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Calendar, Clock, Building2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { projects } from '../mock';

const Projects = () => {
  const [filter, setFilter] = useState('all');

  const categories = ['all', ...Array.from(new Set(projects.map((p) => p.category)))];

  const filteredProjects =
    filter === 'all' ? projects : projects.filter((p) => p.category === filter);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Nos <span className="text-orange-400">Réalisations</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Études de cas concrètes : tonnages transportés, KPIs livrés,
            résultats mesurables pour les leaders industriels du Gabon.
          </p>
        </div>
      </section>

      {/* Filtres + grille */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 md:gap-3 justify-center mb-12">
            {categories.map((category) => (
              <button
                key={category}
                data-testid={`projects-filter-${category}`}
                onClick={() => setFilter(category)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === category
                    ? 'bg-orange-500 text-white shadow-lg scale-105'
                    : 'bg-white text-slate-700 hover:bg-slate-100 shadow'
                }`}
              >
                {category === 'all' ? 'Toutes les réalisations' : category}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card
                key={project.id}
                data-testid={`project-card-${project.id}`}
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group bg-white"
              >
                <div className="h-52 bg-slate-800 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 opacity-95 group-hover:opacity-80 transition-opacity duration-300"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Truck
                      size={64}
                      className="text-orange-400 group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-orange-500 hover:bg-orange-600 text-white">
                      {project.category}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-orange-500 transition-colors leading-snug">
                    {project.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                    {project.description}
                  </p>

                  <div className="space-y-1 border-t border-slate-200 pt-3 mb-4">
                    {project.kpis.map((kpi, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-xs text-slate-700"
                      >
                        <CheckCircle2 size={14} className="text-orange-500" />
                        <span>{kpi}</span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2 border-t border-slate-200 pt-3 text-sm text-slate-600">
                    <div className="flex items-center gap-3">
                      <Calendar size={16} className="text-orange-500" />
                      <span>
                        <strong>{project.year}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock size={16} className="text-orange-500" />
                      <span>
                        Durée : <strong>{project.duration}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building2 size={16} className="text-orange-500" />
                      <span className="truncate">
                        Client : <strong>{project.client}</strong>
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <Truck size={64} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">
                Aucune réalisation dans cette catégorie.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Notre <span className="text-orange-500">empreinte</span> en chiffres
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">
                +50
              </div>
              <div className="text-slate-600">Engins opérationnels</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">
                +200 000 t
              </div>
              <div className="text-slate-600">Transportées / an</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">
                98 %
              </div>
              <div className="text-slate-600">Disponibilité flotte</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">
                24/7
              </div>
              <div className="text-slate-600">Assistance terrain</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Votre projet pourrait être le prochain
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Partagez votre cahier des charges — nous revenons sous 24h avec une
            proposition adaptée.
          </p>
          <Link
            to="/contact"
            data-testid="projects-cta-contact"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            Démarrer un projet
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Projects;
